import axios from 'axios';
import { API_BASE_URL } from '../config';

const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Auth API
export const authAPI = {
  register: (userData: { username: string; email: string; password: string }) =>
    api.post('/auth/register', userData),
  login: (credentials: { email: string; password: string }) =>
    api.post('/auth/login', credentials),
  getCurrentUser: () => api.get('/auth/me'),
};

// Events API
export const eventsAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/events', { params }),
  getPastEvents: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/events/past', { params }),
  getCurrentEvents: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/events/current', { params }),
  getById: (uuid: string) => api.get(`/events/${uuid}`),
  create: (eventData: any) => api.post('/events', eventData),
  update: (uuid: string, eventData: any) => api.put(`/events/${uuid}`, eventData),
  delete: (uuid: string) => api.delete(`/events/${uuid}`),
};

// Users API
export const usersAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/users', { params }),
  getById: (uuid: string) => api.get(`/users/${uuid}`),
  create: (data: any) => api.post('/users', data),
  update: (uuid: string, data: any) => api.put(`/users/${uuid}`, data),
};

// Customers API
export const customersAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/customers', { params }),
  getById: (uuid: string) => api.get(`/customers/${uuid}`),
  create: (data: any) => api.post('/customers', data),
  update: (uuid: string, data: any) => api.put(`/customers/${uuid}`, data),
  delete: (uuid: string) => api.delete(`/customers/${uuid}`),
};

// Transactions API
export const transactionsAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/transactions', { params }),
  getByEventUuid: (eventUuid: string, params?: { page?: number; limit?: number; search?: string }) =>
    api.get(`/transactions/event/${eventUuid}`, { params }),
  getYearlyTotals: () => api.get('/transactions/yearly-totals'),
  create: (data: any) => api.post('/transactions', data),
  update: (uuid: string, data: any) => api.put(`/transactions/${uuid}`, data),
};

// Event Customers API
export const eventCustomersAPI = {
  getByEventId: (eventId: number, params?: { page?: number; limit?: number; search?: string }) =>
    api.get(`/event-customers/${eventId}`, { params }),
  update: (uuid: string, data: any) => api.put(`/event-customers/${uuid}`, data),
};

// Tickets API
export const ticketsAPI = {
  generate: (eventCustomerUuid: string) => api.post('/tickets/generate', { eventCustomerUuid }),
  validate: (ticketUuid: string) => api.get(`/tickets/validate/${ticketUuid}`),
  getByEventCustomer: (eventCustomerUuid: string) => api.get(`/tickets/by-event-customer/${eventCustomerUuid}`),
};

// Reports API
export const reportsAPI = {
  getEventReport: (eventUuid: string) => api.get(`/reports/event/${eventUuid}`),
  getAnnualSummary: () => api.get('/reports/annual-summary'),
  getHighestGrossingEvents: () => api.get('/reports/highest-grossing-events'),
  getHighestPayingCustomers: () => api.get('/reports/highest-paying-customers'),
};

// Config API
export const configAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string }) =>
    api.get('/config', { params }),
  getByUuid: (uuid: string) => api.get(`/config/${uuid}`),
  getByKey: (key: string) => api.get(`/config/key/${key}`),
  create: (data: { key: string; value: string }) => api.post('/config', data),
  update: (uuid: string, data: { key: string; value: string }) => api.put(`/config/${uuid}`, data),
  delete: (uuid: string) => api.delete(`/config/${uuid}`),
};

// Feedback API (public endpoints)
export const feedbackAPI = {
  verifyTicket: (ticket_number: string) => 
    axios.post(`${API_BASE_URL}/feedback/verify-ticket`, { ticket_number }),
  submit: (data: {
    ticket_number: string;
    musical_content: number;
    sound_quality: number;
    ambience: number;
    selection_of_artists: number;
    overall_experience: number;
  }) => axios.post(`${API_BASE_URL}/feedback/submit`, data),
  getByEventUuid: (eventUuid: string) => api.get(`/feedback/event/${eventUuid}`),
};

// Artists API
export const artistsAPI = {
  initiateSubmission: (data: {
    firstName: string;
    lastName: string;
    email: string;
    recaptchaToken: string;
  }) => axios.post(`${API_BASE_URL}/artists/initiate`, data),
  completeSubmission: (data: {
    email: string;
    pin: string;
    youtubeUrl: string;
  }) => axios.post(`${API_BASE_URL}/artists/complete`, data),
  getArtists: (year?: number) => api.get('/artists', { params: { year } }),
  getYears: () => api.get('/artists/years'),
  voteOnArtist: (artistId: number, vote: boolean) => 
    api.post(`/artists/${artistId}/vote`, { vote }),
  getMyVote: (artistId: number) => api.get(`/artists/${artistId}/my-vote`),
  sendDecision: (artistId: number) => api.post(`/artists/${artistId}/send-decision`),
};

// Tasks API
export const tasksAPI = {
  getAll: (params?: { page?: number; limit?: number; search?: string; status?: string; assignedTo?: string }) =>
    api.get('/tasks', { params }),
  getById: (uuid: string) => api.get(`/tasks/${uuid}`),
  create: (data: any) => api.post('/tasks', data),
  update: (uuid: string, data: any) => api.put(`/tasks/${uuid}`, data),
  delete: (uuid: string) => api.delete(`/tasks/${uuid}`),
  addComment: (uuid: string, comment: string) => 
    api.post(`/tasks/${uuid}/comments`, { comment }),
  getStats: () => api.get('/tasks/stats/summary'),
};

export default api;